import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowRight, Calendar, Gauge, MapPin } from "lucide-react";
import {
  fyndanuListingsQuery,
  blocketVehiclesQuery,
  formatPrice,
  formatMil,
  dedupeBlocketAgainstFyndanu,
} from "@/lib/vehicles";

/**
 * Visar de fordon vi faktiskt har i lager som matchar en modellserie.
 * Hämtas på klienten (useQuery, inte suspense) så SSR aldrig väntar på
 * externa annonskällor — seriesidan ska rendera direkt för Google.
 * Renderar en tydlig fallback-CTA när serien inte finns i lager just nu.
 */
export function SerieStock({
  brandName,
  serieName,
  brandSlug,
  category,
}: {
  brandName: string;
  serieName: string;
  brandSlug: string;
  category: "husbil" | "husvagn";
}) {
  const fyndanu = useQuery(fyndanuListingsQuery);
  const blocket = useQuery(blocketVehiclesQuery);

  // Seriens sista ord är modellnamnet ("Kabe Royal" -> "royal",
  // "Rapido 8-serien" -> "8"), märket måste också finnas i titeln.
  const needle = serieName
    .toLowerCase()
    .replace(brandName.toLowerCase(), "")
    .replace(/-serien|serien|klass/g, "")
    .trim();

  const matches = (title: string | null | undefined) => {
    const t = (title ?? "").toLowerCase();
    if (!t.includes(brandName.toLowerCase())) return false;
    return needle.length >= 2 && t.includes(needle);
  };

  const fItems = (fyndanu.data?.items ?? []).filter((f) => matches(f.title));
  const bItems = dedupeBlocketAgainstFyndanu(
    fyndanu.data?.items ?? [],
    blocket.data?.items ?? [],
  ).filter((b) => matches(b.title) && !b.is_sold);

  const total = fItems.length + bItems.length;
  const brandHref = category === "husbil" ? "/husbilar/$brand" : "/husvagnar/$brand";

  return (
    <section className="container-edge pb-14">
      <p className="eyebrow">I lager hos oss</p>
      <h2 className="mt-3 font-display text-3xl md:text-4xl">
        {total > 0 ? (
          <>
            {total} {total === 1 ? "exemplar" : "exemplar"} av{" "}
            <span className="font-italic-display text-accent">{serieName}</span> just nu.
          </>
        ) : (
          <>
            Ingen <span className="font-italic-display text-accent">{serieName}</span> i lager
            just nu.
          </>
        )}
      </h2>

      {total > 0 ? (
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {fItems.slice(0, 6).map((f) => (
            <a
              key={f.id}
              href={f.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-xl"
            >
              {f.image && (
                <img
                  src={f.image}
                  alt={f.title}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
              )}
              <div className="p-5">
                <h3 className="font-display text-lg leading-tight">{f.title}</h3>
                <p className="mt-2 font-display text-xl text-accent">{formatPrice(f.price)}</p>
                <p className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {f.year && (
                    <span className="inline-flex items-center gap-1">
                      <Calendar size={12} /> {f.year}
                    </span>
                  )}
                  {formatMil(f.mileage) && (
                    <span className="inline-flex items-center gap-1">
                      <Gauge size={12} /> {formatMil(f.mileage)}
                    </span>
                  )}
                  {f.location && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin size={12} /> {f.location}
                    </span>
                  )}
                </p>
              </div>
            </a>
          ))}
          {bItems.slice(0, Math.max(0, 6 - fItems.length)).map((b) => (
            <a
              key={b.external_id}
              href={b.listing_url}
              target="_blank"
              rel="noopener noreferrer"
              className="group overflow-hidden rounded-3xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-xl"
            >
              {b.image_url && (
                <img
                  src={b.image_url}
                  alt={b.title ?? serieName}
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                />
              )}
              <div className="p-5">
                <h3 className="font-display text-lg leading-tight">{b.title}</h3>
                <p className="mt-2 font-display text-xl text-accent">{formatPrice(b.price)}</p>
              </div>
            </a>
          ))}
        </div>
      ) : (
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground">
          Vi omsätter lagret löpande och tar in {serieName} i inbyte flera gånger per år. Säg till
          vad du letar efter — vi ringer när rätt exemplar kommer in, oftast innan det annonseras.
        </p>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <Link
          to={brandHref}
          params={{ brand: brandSlug }}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-semibold transition hover:bg-secondary"
        >
          Alla {brandName} i lager <ArrowRight size={14} />
        </Link>
        <Link
          to="/kontakt"
          className="inline-flex items-center gap-2 btn-shine rounded-full bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
        >
          Bevaka {serieName} <ArrowRight size={14} />
        </Link>
      </div>
    </section>
  );
}
